import React, { useMemo } from 'react';
import { Modal } from 'framework/ui/ui-builder';
import { CreateForm } from 'framework/form';
import { useModalHandler } from 'framework/ui/dashboard';
import FollowUpFormContent from './createUpdate/FollowUpFormContent';

interface FollowUpItem {
  id?: string | number;
  description: string;
  followUpDate: string;
  [key: string]: unknown;
}

interface ValidationSchema {
  validate: (data: unknown, options?: { abortEarly?: boolean }) => Promise<unknown>;
}

interface FollowUpModalCallbacks {
  onSubmit: (followUp: Partial<FollowUpItem>) => Promise<FollowUpItem>;
  onClose: () => void;
  getValidationSchemaFunc?: () => Promise<ValidationSchema>;
}

interface FollowUpModalProps {
  isOpen: boolean;
  data?: FollowUpItem;
  onClose: () => void;
  globalData?: Record<string, unknown>;
  getDataFunc?: () => Record<string, unknown>;
  submitFormFunc?: (data: Record<string, unknown>) => Promise<FollowUpItem>;
  getValidationSchemaFunc?: () => Promise<ValidationSchema>;
  afterSubmit?: { execute: (data?: unknown) => void };
  followUpForUpdate?: FollowUpItem;
  callbacks?: FollowUpModalCallbacks;
}

const FollowUpModal: React.FC<FollowUpModalProps> = ({
  isOpen,
  data,
  onClose,
  submitFormFunc,
  afterSubmit,
  followUpForUpdate,
  callbacks,
  getValidationSchemaFunc,
}) => {
  const itemToUpdate = followUpForUpdate || data;
  const isUpdateMode = !!itemToUpdate?.id;

  const initialFormData = useMemo(() => {
    if (itemToUpdate) {
      return {
        description: itemToUpdate.description || '',
        followUpDate: itemToUpdate.followUpDate || '',
        id: itemToUpdate.id,
      };
    }
    return { description: '', followUpDate: '' };
  }, [itemToUpdate]);

  const { handleAfterSubmit, handleFormSubmit } = useModalHandler({
    onClose,
    afterSubmit,
    submitFormFunc: async submitData => {
      if (submitFormFunc) {
        return await submitFormFunc(submitData);
      }
      if (callbacks?.onSubmit) {
        return await callbacks.onSubmit(submitData);
      }
      return submitData;
    },
    getValidationSchemaFunc,
  });

  if (!isOpen) return null;

  return (
    <CreateForm
      submitFormFunc={handleFormSubmit}
      getValidationSchemaFunc={getValidationSchemaFunc}
      initialData={initialFormData}
      afterSubmit={handleAfterSubmit}
    >
      <Modal
        paperStyles={{
          background: 'var(--mui-palette-background-paper)',
        }}
        isOpen={isOpen}
        onToggle={onClose || callbacks?.onClose}
        hasCancelButton={false}
      >
        <FollowUpFormContent onClose={onClose} isUpdateMode={isUpdateMode} />
      </Modal>
    </CreateForm>
  );
};

export default FollowUpModal;
